import { isOIDCEnabled, debugLog } from './OIDCConfig';
import { FEATURE_FLAGS } from './FeatureFlags';

/**
 * Interface for ALB OIDC headers
 */
export interface ALBOIDCHeaders {
  accessToken: string;
  identity: string;
  data: string;
}

/**
 * Interface for server response containing JWT token
 */
export interface OIDCTokenResponse {
  success: boolean;
  token?: string;
  error?: string;
  message?: string;
}

/**
 * Check if current environment is a preview subdomain
 * Preview URLs follow the pattern: preview.cms-{environment}.theinstitutes.org
 * @returns boolean indicating if on preview subdomain
 */
const isPreviewEnvironment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname.startsWith('preview.cms');
};

/**
 * Check if token query parameter exists in URL
 * @returns boolean indicating if token parameter exists
 */
const hasTokenParameter = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('token');
};

/**
 * Extract ALB OIDC headers from various sources
 * In a browser environment, these are:
 * 1. Passed through meta tags set by nginx (primary method)
 * 2. Optionally stored in sessionStorage by nginx-injected script (legacy/testing)
 *
 * NOTE: As of June 2025, with the new JWT cookie approach, OIDC headers are automatically processed by nginx to set JWT cookies. Client-side code should primarily rely on the JWT cookie rather than manually extracting and processing OIDC headers.
 *
 * @returns ALBOIDCHeaders or null if not available
 */
const extractALBOIDCHeaders = async (): Promise<ALBOIDCHeaders | null> => {
  debugLog('=== Starting ALB OIDC headers extraction ===');

  // Enhanced debugging: Log all meta tags for analysis
  debugLog('Analyzing all meta tags in document head');
  const allMetaTags = document.querySelectorAll('meta');
  debugLog(`Total meta tags found: ${allMetaTags.length}`);

  let oidcMetaTagsFound = 0;
  allMetaTags.forEach((meta, index) => {
    const name = meta.getAttribute('name');
    const content = meta.getAttribute('content');
    if (name && name.includes('oidc')) {
      oidcMetaTagsFound++;
      debugLog(
        `OIDC Meta tag ${index}: name="${name}", content length=${content?.length || 0}`,
      );
    }
  });

  if (oidcMetaTagsFound === 0) {
    debugLog('No OIDC-related meta tags found in document');
  }

  // Enhanced debugging: Check if ALB OIDC injection comment is present
  const headElement = document.head;
  if (headElement) {
    const headHTML = headElement.innerHTML;
    const hasOIDCComment = headHTML.includes('ALB OIDC Headers Injection');
    const hasOIDCMeta = headHTML.includes('x-amzn-oidc');
    debugLog(`Head HTML contains ALB OIDC comment: ${hasOIDCComment}`);
    debugLog(`Head HTML contains OIDC meta tags: ${hasOIDCMeta}`);

    if (hasOIDCComment && !hasOIDCMeta) {
      debugLog(
        'WARNING: OIDC comment found but meta tags missing - possible sub_filter issue',
      );
    }
  }

  // Method 1: Check for headers stored in meta tags (set by nginx)
  debugLog('Checking meta tags for OIDC headers');
  const accessTokenMeta = document.querySelector(
    'meta[name="x-amzn-oidc-accesstoken"]',
  );
  const identityMeta = document.querySelector(
    'meta[name="x-amzn-oidc-identity"]',
  );
  const dataMeta = document.querySelector('meta[name="x-amzn-oidc-data"]');

  debugLog(`Access token meta found: ${!!accessTokenMeta}`);
  debugLog(`Identity meta found: ${!!identityMeta}`);
  debugLog(`Data meta found: ${!!dataMeta}`);

  if (accessTokenMeta && identityMeta && dataMeta) {
    const accessToken = accessTokenMeta.getAttribute('content') || '';
    const identity = identityMeta.getAttribute('content') || '';
    const data = dataMeta.getAttribute('content') || '';

    debugLog('Found OIDC headers in meta tags', {
      accessTokenLength: accessToken.length,
      identityLength: identity.length,
      dataLength: data.length,
    });

    return {
      accessToken,
      identity,
      data,
    };
  }

  // Method 2: Check sessionStorage (could be set by nginx-injected script)
  debugLog('Checking sessionStorage for OIDC headers');
  const sessionAccessToken = sessionStorage.getItem('x-amzn-oidc-accesstoken');
  const sessionIdentity = sessionStorage.getItem('x-amzn-oidc-identity');
  const sessionData = sessionStorage.getItem('x-amzn-oidc-data');

  debugLog(`SessionStorage access token found: ${!!sessionAccessToken}`);
  debugLog(`SessionStorage identity found: ${!!sessionIdentity}`);
  debugLog(`SessionStorage data found: ${!!sessionData}`);

  if (sessionAccessToken && sessionIdentity && sessionData) {
    debugLog('Found OIDC headers in sessionStorage', {
      accessTokenLength: sessionAccessToken.length,
      identityLength: sessionIdentity.length,
      dataLength: sessionData.length,
    });

    return {
      accessToken: sessionAccessToken,
      identity: sessionIdentity,
      data: sessionData,
    };
  }

  // Enhanced debugging: Check window.oidcDebugInfo if available
  if (typeof window !== 'undefined' && (window as any).oidcDebugInfo) {
    debugLog('Found window.oidcDebugInfo:', (window as any).oidcDebugInfo);
  } else {
    debugLog(
      'No window.oidcDebugInfo found - nginx injection may not have occurred',
    );
  }

  debugLog('No OIDC headers found in any source (meta tags or sessionStorage)');
  return null;
};

/**
 * Generate JWT token by making a request to the OIDC endpoint
 *
 * NOTE: As of June 2025, this function is largely obsolete due to the new JWT cookie approach where nginx automatically handles JWT generation and cookie setting. This function is retained for backwards compatibility and manual testing.
 *
 * @param oidcHeaders - The ALB OIDC headers (from meta tags)
 * @returns Promise<string | null> - The generated JWT token or null if failed
 */
const generateJWTFromOIDC = async (
  oidcHeaders: ALBOIDCHeaders,
): Promise<string | null> => {
  try {
    debugLog('Sending request to OIDC API Gateway endpoint');

    // Always use the internal JWT endpoint - runs locally in the container
    const endpoint = '/internal/jwt';

    debugLog('OIDC headers available (from meta tags):', {
      accessTokenLength: oidcHeaders.accessToken?.length || 0,
      identityLength: oidcHeaders.identity?.length || 0,
      dataLength: oidcHeaders.data?.length || 0,
    });

    // Send OIDC headers as required by API Gateway authorizer
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-amzn-oidc-accesstoken': oidcHeaders.accessToken,
        'x-amzn-oidc-identity': oidcHeaders.identity,
        'x-amzn-oidc-data': oidcHeaders.data,
      },
      body: JSON.stringify({
        timestamp: new Date().toISOString(),
        requestId: Math.random().toString(36).substring(2, 15),
      }),
    });

    if (!response.ok) {
      throw new Error(
        `OIDC API Gateway request failed: ${response.status} ${response.statusText}`,
      );
    }

    const result: OIDCTokenResponse = await response.json();

    if (result.success && result.token) {
      debugLog('Successfully generated JWT token via API Gateway');
      return result.token;
    } else {
      console.error(
        'OIDC API Gateway returned error:',
        result.error || result.message,
      );
      return null;
    }
  } catch (error) {
    console.error('Failed to generate JWT via OIDC API Gateway:', error);
    return null;
  }
};

/**
 * Store JWT token in appropriate location (cookie and localStorage)
 * Uses consistent 'jwt' cookie name across the application
 * @param token - The JWT token to store
 */
const storeJWTToken = (token: string): void => {
  try {
    // Store in both cookie and localStorage for flexibility
    const expires = new Date();
    expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000); // 24 hours

    // Use 'jwt' cookie name to match KeyLoader.ts expectations
    document.cookie = `jwt=${token}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
    localStorage.setItem('jwt_token', token);

    debugLog('JWT token stored successfully in cookie and localStorage');
  } catch (error) {
    console.error('Failed to store JWT token:', error);
  }
};

/**
 * Get JWT token from cookie (consistent with KeyLoader.ts)
 * @returns JWT token string or null
 */
const getJWTFromCookie = (): string | null => {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'jwt') {
        return value;
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to get JWT token from cookie:', error);
    return null;
  }
};

/**
 * Clear JWT token from all storage locations
 */
const clearJWTToken = (): void => {
  try {
    // Clear cookie
    document.cookie = 'jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    // Clear localStorage
    localStorage.removeItem('jwt_token');
    debugLog('JWT token cleared from all storage locations');
  } catch (error) {
    console.error('Failed to clear JWT token:', error);
  }
};

/**
 * Main function to process ALB OIDC authentication
 *
 * IMPORTANT: As of June 2025, this function is largely obsolete due to the new JWT cookie approach.
 * With the new system, nginx automatically:
 * 1. Detects when OIDC headers are present and JWT cookie is missing
 * 2. Calls the internal JWT service to generate the cookie
 * 3. Sets the JWT cookie automatically in the response
 *
 * This function is retained for backwards compatibility and manual testing, but normal application flow should rely on the automatic JWT cookie setting.
 *
 * @returns Promise<string | null> - The JWT token if successful, null otherwise
 * @deprecated Use automatic JWT cookie handling via nginx instead
 */
const processALBOIDCAuth = async (): Promise<string | null> => {
  debugLog('=== Starting ALB OIDC authentication process ===');
  debugLog(`Current hostname: ${window.location.hostname}`);
  debugLog(`Current search params: ${window.location.search}`);

  // Check if OIDC is enabled
  if (!isOIDCEnabled()) {
    debugLog('OIDC is disabled, skipping authentication');
    return null;
  }
  // Only process for preview environments
  if (!isPreviewEnvironment()) {
    debugLog('Not in preview environment, skipping OIDC processing');
    return null;
  }

  debugLog('Preview environment confirmed, continuing with OIDC processing');

  // Skip if token parameter is already present
  if (hasTokenParameter()) {
    debugLog('Token parameter already present, skipping OIDC processing');
    return null;
  }

  try {
    // Extract ALB OIDC headers
    debugLog('Attempting to extract ALB OIDC headers...');
    const oidcHeaders = await extractALBOIDCHeaders();
    if (!oidcHeaders) {
      debugLog('No ALB OIDC headers available, skipping authentication');
      return null;
    }

    debugLog('ALB OIDC headers extracted successfully', {
      hasAccessToken: !!oidcHeaders.accessToken,
      hasIdentity: !!oidcHeaders.identity,
      hasData: !!oidcHeaders.data,
      accessTokenLength: oidcHeaders.accessToken?.length || 0,
      identityLength: oidcHeaders.identity?.length || 0,
      dataLength: oidcHeaders.data?.length || 0,
    });

    // Generate JWT token via Lambda
    debugLog('Sending OIDC headers to Lambda for JWT generation...');
    const jwtToken = await generateJWTFromOIDC(oidcHeaders);
    if (!jwtToken) {
      console.error('Failed to generate JWT token from OIDC headers');
      return null;
    }

    // Store the token
    storeJWTToken(jwtToken);

    debugLog('=== ALB OIDC authentication completed successfully ===');
    return jwtToken;
  } catch (error) {
    console.error('Error during ALB OIDC authentication:', error);
    return null;
  }
};

/**
 * Manual trigger for OIDC authentication - useful for debugging
 * This function can be called from browser console to test OIDC flow
 * @returns Promise<string | null>
 */
const triggerOIDCAuth = async (): Promise<string | null> => {
  debugLog('=== Manual OIDC authentication trigger ===');
  return await processALBOIDCAuth();
};

/**
 * Interface for OIDC debug state
 */
export interface OIDCDebugState {
  hostname: string;
  search: string;
  isPreviewEnvironment: boolean;
  hasTokenParameter: boolean;
  isOIDCEnabled: boolean;
  existingJWTCookie: string | null;
}

/**
 * Debug function to show current OIDC state
 * Call this from browser console to see current OIDC status
 * Only available when debug features are enabled
 */
const debugOIDCState = (): OIDCDebugState => {
  const state: OIDCDebugState = {
    hostname: window.location.hostname,
    search: window.location.search,
    isPreviewEnvironment: isPreviewEnvironment(),
    hasTokenParameter: hasTokenParameter(),
    isOIDCEnabled: isOIDCEnabled(),
    existingJWTCookie: getJWTFromCookie(),
  };

  // Only log to console if debug features are allowed
  if (typeof window !== 'undefined' && FEATURE_FLAGS.ALLOW_OIDC_DEBUG) {
    console.log('=== OIDC Debug State ===', state);
  }

  return state;
};

// Export all functions at the end of the file
export {
  clearJWTToken,
  debugOIDCState,
  extractALBOIDCHeaders,
  getJWTFromCookie,
  generateJWTFromOIDC,
  hasTokenParameter,
  isPreviewEnvironment,
  processALBOIDCAuth,
  triggerOIDCAuth,
  storeJWTToken,
};
